import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useMutationState, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Button, useToast } from '@vd/ui';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth, useCurrentCompany } from '../../../features/auth/AuthProvider';
import { api } from '../../../lib/api';
import styles from '../Settings.module.css';
import picker from './JiraTab.module.css';
import { JiraExportHelp, JiraNeedsTeam } from '../../../features/jira/JiraExportHelp';
import { JiraProjectsNextStep } from '../../../features/jira/JiraProjectsNextStep';

type State = Awaited<ReturnType<typeof api.getJiraConnection>>;
/** What the tab knows of a workspace: a status read, or, after a refusal with none, only its plan. */
type Status = State | { status: 'unknown'; planHasJira: false };
type Pending = Awaited<ReturnType<typeof api.getJiraPending>>;
/** A message on the tab, about one workspace: the address's own, about the one on screen when it arrived. */
type Notice = { kind: 'error' | 'success'; message: string; companyId: string | null };
type Message = Omit<Notice, 'companyId'>;

const CALLBACK_ERRORS: Record<string, string> = {
  jira_not_configured: 'Jira is not configured for this Toado environment.',
  invalid_oauth_callback: 'Jira returned an incomplete authorization response. Please try connecting again.',
  invalid_oauth_state: 'The Jira connection request expired or was already used. Please try again.',
  jira_oauth_failed: 'Jira authorization failed. Please try again or ask your Atlassian administrator for access.',
  jira_refresh_missing: 'Jira did not grant offline access, so Toado cannot keep the connection active.',
  jira_scopes_missing: 'Jira did not grant the required read and write permissions.',
  jira_owner_required: 'Only a workspace owner can finish connecting Jira. Ask an owner to start a new connection.',
  jira_busy: 'Jira settings were being changed at the same moment. Connect again.',
  jira_round_replaced: 'Another Jira sign-in for this workspace replaced this one, or Jira was disconnected meanwhile. Connect again if you still want this one.',
  jira_no_sites: 'This Atlassian account has no Jira sites. Sign in to Atlassian with an account that belongs to the Jira site you want to connect.',
  // The workspace's plan changed while its owner was at Atlassian (TOADO-713).
  jira_requires_team: 'Jira export is on the Team plan and up, so Jira was not connected.',
  // No longer sent: a sign-in that reaches several sites now offers a choice
  // (TOADO-262). Kept for a bookmarked address.
  jira_site_selection_required: 'This Atlassian account can reach more than one Jira site. Connect again to choose one.',
};
const EXPIRED = 'That sign-in has expired. Connect again.';
// The server answers 404 alike for a sign-in used, expired or not this
// person's, so the page does not claim which (z.ai review).
const GONE = 'That sign-in is no longer available. Connect again to choose a site.';
const NOT_YOURS = 'That Jira sign-in was for a workspace you are no longer a member of.';
/** How often the time left is counted down while the picker is open: often enough that it closes within a second of its time. */
const TICK_MS = 1_000;

const isNotFound = (failure: unknown) => (failure as { status?: number } | null)?.status === 404;
/** The workspace's plan has no Jira export (TOADO-713): no retry can succeed, so the tab shows why instead. */
const needsTeam = (failure: unknown) => (failure as { code?: string } | null)?.code === 'jira_requires_team';

/**
 * Whether nothing has the focus: the page has it, as it does once a focused
 * control leaves the page. The tab moves the focus only then, or from what it
 * is closing, so it never takes the focus from somewhere the person put it
 * (Codex review round 9).
 */
function focusIsFree() {
  const active = document.activeElement;
  return !active || active === document.body;
}

function host(url: string) {
  try { return new URL(url).host; } catch { return url; }
}

/** The cache key of one workspace's Jira connection. */
const connectionKey = (companyId: string) => ['jira-connection', companyId] as const;
/** The key every Disconnect is filed under, so the tab can find each workspace's own. */
const DISCONNECT = ['jira-disconnect'] as const;

/**
 * Brings one workspace's status back to what the server says after a change
 * (Codex review round 7). A read already under way began before the change, so
 * it is cancelled rather than trusted; what the change's own answer says the
 * status now is (`known`) is shown meanwhile; then the status is read again.
 * That read is not waited for: a change has settled once its own answer is
 * in, and a slow read must not hold its button or hide its failure (Codex
 * review round 8). Cancelling is not a wait: it ends the read at once.
 */
async function reconcile(queryClient: QueryClient, companyId: string, known?: State) {
  const key = connectionKey(companyId);
  await queryClient.cancelQueries({ queryKey: key });
  // A change's answer says nothing of the plan, which it did not change: the
  // plan already known stays, so Disconnect below Team does not bring back
  // Connect while the read is out, or for good if it fails (TOADO-713, Codex
  // review round 2).
  // A refusal for the plan is already in the status it would keep (refusedForPlan).
  if (known) queryClient.setQueryData<State>(key, (before) => ({ ...known, planHasJira: known.planHasJira ?? before?.planHasJira }));
  void queryClient.invalidateQueries({ queryKey: key });
}

/**
 * The server refused for the plan (TOADO-713): that is the plan's answer, and
 * it goes into the workspace's status, the one place the tab reads the plan
 * from. With no status read yet, the connection is written as `unknown`. The
 * next read that finishes, on this tab or the mapping tab, replaces it. One
 * under way now is cancelled first: invalidating cancels only the reads of
 * the workspace on screen, and a refusal can come for one switched away from
 * (Codex review rounds 4 to 8).
 */
function refusedForPlan(queryClient: QueryClient, companyId: string) {
  const key = connectionKey(companyId);
  void queryClient.cancelQueries({ queryKey: key }, { revert: false });
  queryClient.setQueryData<Status>(key, (before) => ({ ...(before ?? { status: 'unknown' }), planHasJira: false }));
  void queryClient.invalidateQueries({ queryKey: key });
}

/** Whole minutes left to choose a site, never fewer than one while it is offered. */
export function minutesLeft(msLeft: number): number {
  return Math.max(1, Math.ceil(msLeft / 60_000));
}

/** What switching to another site costs: its mappings carry the old site and go stale. */
export function remapWarning(count: number, currentSiteUrl: string): string {
  return `${count} ${count === 1 ? 'project is' : 'projects are'} mapped to ${host(currentSiteUrl)} and will need mapping again.`;
}

/**
 * The Jira tab. The connection's status is one query per workspace, so an
 * answer for one workspace never lands on another, and every change is
 * followed by a fresh read from the server, which has the last word.
 *
 * A sign-in that reaches several Jira sites lands here with ?select=1 and the
 * workspace it was for (TOADO-262), and the address decides whether the
 * picker shows. The picker is its own component, keyed by the history entry
 * and the workspace: its sites, choice, countdown and requests in flight all
 * belong to that one appearance and end with it. Six review rounds of races
 * between pieces of state shared across appearances are why it is built so.
 *
 * It waits for the app to have a current workspace, so everything in it is
 * about one from its first render: a message the address brought belongs to
 * the workspace on screen when it arrived, never to all of them (z.ai review
 * round 3).
 */
export function JiraTab() {
  const company = useCurrentCompany();
  return company ? <JiraSettings company={company} /> : null;
}

function JiraSettings({ company }: { company: NonNullable<ReturnType<typeof useCurrentCompany>> }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { companies, setCurrentCompany } = useAuth();
  const queryClient = useQueryClient();
  const companyId = company.id;
  const [notice, setNotice] = useState<Notice | null>(() => searchParams.get('error')
    ? { kind: 'error', message: CALLBACK_ERRORS[searchParams.get('error')!] ?? 'Jira could not be connected.', companyId }
    : searchParams.get('connected') === '1'
      ? { kind: 'success', message: 'Jira connected successfully.', companyId }
      : null);
  // An action that failed (Connect, Disconnect, a choice, Cancel) says so in a
  // red toast, at the moment it fails (Jason, 13 Oct). What the tab is in, such
  // as a sign-in that is gone or the address's own message, stays on the tab.
  const toast = useToast();
  // The history entry whose picker was closed: hidden from that moment, while
  // the router's own update of the address may still be on its way. The router
  // makes that update a transition, so in a browser the closed picker could
  // otherwise paint again for a frame. No test sees that window, since under
  // act the navigation commits before anything is asserted: removing this
  // keeps the suite green and brings the flash back.
  const [closedEntry, setClosedEntry] = useState<string | null>(null);

  const connection = useQuery<Status>({
    queryKey: connectionKey(companyId),
    queryFn: () => api.getJiraConnection(companyId),
  });
  const disconnect = useMutation({
    mutationKey: DISCONNECT,
    mutationFn: (id: string) => api.disconnectJira(id),
    onSuccess: (_answer, id) => reconcile(queryClient, id, { status: 'not_connected' }),
    onError: (_failure, id) => {
      // A red toast stays until dismissed, and the answer can come after a
      // switch to another workspace, so it names the one it was for (Codex
      // review round 8).
      const name = companies.find((candidate) => candidate.id === id)?.name;
      toast.push({ variant: 'danger', title: name ? `Could not disconnect Jira from ${name}` : 'Could not disconnect Jira', description: 'Please try again.' });
      return reconcile(queryClient, id);
    },
  });
  // Each workspace's Disconnects still out, from the mutation cache: the hook's
  // own state follows only its latest call, which may have been for another
  // workspace (Codex review round 7).
  const disconnects = useMutationState({
    filters: { mutationKey: DISCONNECT },
    select: (mutation) => ({ companyId: mutation.state.variables as string, status: mutation.state.status }),
  }).filter((entry) => entry.companyId === companyId);
  // What is asking (the Switch prompt or the picker). When it closes with the
  // focus in it, in the notice, or nowhere, the focus goes to the notice, or
  // to the heading when there is none, rather than falling to the page (z.ai
  // review round 3).
  // A close counts in `landings`, state rather than a ref, so the focus moves
  // only once the render carrying the close's own notice is on the page; a
  // close made from an effect would otherwise move it a render early (Codex
  // review round 9).
  const asking = useRef<HTMLDivElement>(null);
  const noticeLine = useRef<HTMLParagraphElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const [landings, setLandings] = useState(0);

  /**
   * Takes names out of the address. The picker always calls the latest render's
   * `close`, so this is always the latest render's setter, built from the
   * address as it is now.
   */
  const forget = (...names: string[]) => setSearchParams((latest) => {
    const next = new URLSearchParams(latest);
    for (const name of names) next.delete(name);
    return next;
  }, { replace: true });

  // ?error and ?connected became the notice; the address forgets them.
  useEffect(() => {
    if (searchParams.has('error') || searchParams.has('connected')) forget('error', 'connected');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival
  }, []);

  const asked = searchParams.get('select') === '1' && closedEntry !== location.key;
  const signedInFor = asked ? (searchParams.get('workspace') ?? companyId) : null;
  const signingWorkspace = companies.find((candidate) => candidate.id === signedInFor);

  /** Ends this entry's picker, saying `message` about `about`, or nothing when there is none. */
  const close = (message: Message | null, about: string | null) => {
    // The close takes away what is asking, and replaces or clears the notice
    // (Codex review round 10): a focus in either would fall to the page.
    const active = document.activeElement;
    if (focusIsFree() || asking.current?.contains(active) || noticeLine.current?.contains(active)) setLandings((count) => count + 1);
    setClosedEntry(location.key);
    forget('select', 'workspace');
    setNotice(message && { ...message, companyId: about });
  };
  // A sign-in for a workspace this person does not belong to opens nothing.
  useEffect(() => {
    if (signedInFor !== null && !signingWorkspace) close({ kind: 'error', message: NOT_YOURS }, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close only acts on this entry
  }, [signedInFor, signingWorkspace]);
  const picking = signedInFor === companyId;
  useEffect(() => {
    if (landings > 0) (noticeLine.current ?? heading.current)?.focus();
  }, [landings]);

  const connect = async () => {
    const pressed = document.activeElement;
    try { window.location.assign((await api.connectJira(company.id)).authorizationUrl); }
    catch (failure) {
      // A page from before the plan changed: the status read again brings the
      // explainer, and the notice says what happened (Codex review round 1).
      // Connect leaves with the read, so a focus on it, or nowhere, goes to the
      // notice, as a close does (round 3).
      if (needsTeam(failure)) {
        if (focusIsFree() || document.activeElement === pressed) setLandings((count) => count + 1);
        setNotice({ kind: 'error', message: CALLBACK_ERRORS.jira_requires_team!, companyId: company.id });
        refusedForPlan(queryClient, company.id);
        return;
      }
      toast.push({ variant: 'danger', title: `Could not start Jira authorization for ${company.name}`, description: 'Please try again.' });
    }
  };
  const shownNotice = notice && (notice.companyId === null || notice.companyId === company.id) ? notice : null;
  const state = connection.data;
  const disconnecting = disconnects.some((entry) => entry.status === 'pending');

  // The browser has moved to another workspace since Connect was pressed. The
  // prompt sits above this workspace's own status, which stays usable, and
  // can be declined (z.ai review round 3).
  const elsewhere = signedInFor !== null && signedInFor !== company.id ? signingWorkspace : undefined;
  const prompt = elsewhere && (
    <div ref={asking} className={picker.elsewhere}>
      <p role="status">
        This Jira sign-in was started for <strong>{elsewhere.name}</strong>. Switch to {elsewhere.name} to choose which Jira site it connects to.
      </p>
      <div className={styles.row}>
        <Button variant="primary" onClick={() => setCurrentCompany(elsewhere.id)}>
          Switch to {elsewhere.name}
        </Button>
        <Button variant="secondary" onClick={() => close(null, null)}>Stay on {company.name}</Button>
      </div>
    </div>
  );

  // Below the Team plan (TOADO-713): what Jira export does, and the way to the
  // Team plan. A connection made before stays visible, and can be ended, and a
  // sign-in started for another workspace still offers the switch to it. The
  // plan is known from the status, or from a refusal no read has answered since.
  const belowTeam = state?.planHasJira === false && !picking;

  let body;
  if (picking) {
    body = <div ref={asking}>
      <SitePicker key={`${location.key}:${signedInFor}`} companyId={company.id}
        onClose={(message) => close(message, company.id)}
        onConnected={(siteUrl) => close({ kind: 'success', message: `Jira connected to ${host(siteUrl)}.` }, company.id)} />
    </div>;
  } else if (!state) {
    body = connection.isError ? <>
      <p role="alert">Could not load Jira connection.</p>
      <Button onClick={() => void connection.refetch()}>Retry</Button>
    </> : <p role="status">Loading...</p>;
  } else if (belowTeam) {
    if (state.status === 'connected') {
      body = <>
        <p>Connected to <strong>{state.siteUrl}</strong>. Tickets already in Jira keep their links; sending more needs the Team plan.</p>
        <Button variant="secondary" disabled={disconnecting} onClick={() => disconnect.mutate(company.id)}>
          {disconnecting ? 'Disconnecting...' : 'Disconnect Jira'}
        </Button>
      </>;
    } else if (state.status === 'unknown' && connection.isError) {
      // Only the plan is known: whether a connection was made before is not.
      body = <>
        <p role="alert">Could not load Jira connection.</p>
        <Button onClick={() => void connection.refetch()}>Retry</Button>
      </>;
    } else body = null;
  } else if (state.status === 'connected') {
    body = <>
      <p>Connected to <strong>{state.siteUrl}</strong>.</p>
      <DeletedIssuesLine watch={state.deletedIssues} onConnectAgain={() => void connect()} />
      <Button variant="secondary" disabled={disconnecting} onClick={() => disconnect.mutate(company.id)}>
        {disconnecting ? 'Disconnecting...' : 'Disconnect Jira'}
      </Button>
    </>;
  } else {
    body = <>
      {state.status === 'disconnected' && <p role="alert">Jira is disconnected because authorization expired or was revoked. Reconnect to resume exports.</p>}
      <Button onClick={() => void connect()}>{state.status === 'disconnected' ? 'Reconnect Jira' : 'Connect Jira'}</Button>
    </>;
  }

  // One layout on every plan, so the notice is the same element when the plan
  // arrives and a focus on it survives (Codex review round 2 of TOADO-713).
  // Below Team the card is hidden while it has nothing to show.
  return <>
    <h1 ref={heading} tabIndex={-1} className={styles.h1}>Jira</h1>
    {!belowTeam && <p className={styles.sub}>Connect this workspace to Jira Cloud. Exported descriptions, reporter details, and images leave Toado and are stored in your Atlassian site. <JiraExportHelp /></p>}
    <section className={styles.section} hidden={belowTeam && !shownNotice && !prompt && !body}>
    {shownNotice && <p ref={noticeLine} tabIndex={-1} role={shownNotice.kind === 'error' ? 'alert' : 'status'}>{shownNotice.message}</p>}
    {prompt}
    {body}
    </section>
    {belowTeam && <JiraNeedsTeam companyId={company.id} />}
    {/* Connected, so the next thing to do is map each project (t-HUlenl). */}
    {!belowTeam && !picking && state?.status === 'connected' && <JiraProjectsNextStep />}
  </>;
}

/**
 * One appearance of the site picker, for one workspace on one history entry.
 * Everything it holds, and every request it has in flight, ends when it
 * unmounts: an answer that lands afterwards changes nothing on the page but
 * the workspace's connection status, which it has read again.
 */
function SitePicker({ companyId, onClose, onConnected }: {
  companyId: string;
  onClose: (message: Message | null) => void;
  onConnected: (siteUrl: string) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<Pending | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [deadline, setDeadline] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState<'connecting' | 'cancelling' | null>(null);
  // Whether this appearance is still on the page, and its latest callbacks.
  const mounted = useRef(false);
  const sites = useRef<HTMLFieldSetElement>(null);
  const latest = useRef({ onClose, onConnected });
  useLayoutEffect(() => { latest.current = { onClose, onConnected }; });
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // A 404 ends the picker; any other failure leaves the sign-in, which may
  // still be good, where Retry can reach it (z.ai review).
  useEffect(() => {
    let live = true;
    setLoadFailed(false);
    // Counted from the moment the sites were asked for, not from the answer:
    // the time the server measured has been passing ever since, so an answer
    // that took long errs on the early side (Codex review round 13).
    const asked = Date.now();
    api.getJiraPending(companyId).then((offer) => {
      if (!live) return;
      setNow(Date.now());
      setDeadline(asked + offer.secondsLeft * 1000);
      setPending(offer);
    }, (failure) => {
      if (!live) return;
      if (!isNotFound(failure)) { setLoadFailed(true); return; }
      // Gone can mean another tab disconnected Jira, which ends every sign-in,
      // so the status beside it is read again too (Codex review round 8).
      void reconcile(queryClient, companyId);
      latest.current.onClose({ kind: 'error', message: GONE });
    });
    return () => { live = false; };
  }, [companyId, attempt, queryClient]);
  // The sites take the focus when they arrive, if nothing has it: after
  // Switch, whose button has left the page, after Retry, or on arrival from
  // Atlassian. A focus the person has put somewhere stays there (Codex review
  // rounds 8 and 9, z.ai review round 4).
  useEffect(() => {
    if (pending && focusIsFree()) sites.current?.focus();
  }, [pending]);
  /**
   * A choice or Cancel that failed leaves the picker open and says so in a red
   * toast. The button disabled while it was answered may have let the focus
   * go: then it goes back to the sites rather than to the page (z.ai review
   * round 4).
   */
  const failed = (title: string) => {
    toast.push({ variant: 'danger', title, description: 'Please try again.' });
    if (focusIsFree()) sites.current?.focus();
  };
  // The time left counts down, from the seconds the server reported on its
  // own clock, and the picker closes when it runs out, unless a choice or
  // Cancel is being answered: then the answer decides, and an answer that
  // leaves the picker open past its time closes it at once rather than
  // offering a retry that cannot succeed (z.ai web review round 5).
  useEffect(() => {
    if (!pending || busy) return;
    const tick = () => {
      const at = Date.now();
      if (at < deadline) setNow(at);
      else latest.current.onClose({ kind: 'error', message: EXPIRED });
    };
    tick();
    const timer = setInterval(tick, TICK_MS);
    return () => clearInterval(timer);
  }, [pending, deadline, busy]);

  if (!pending) {
    return loadFailed ? <>
      <p role="alert">Could not load the Jira sites.</p>
      <Button onClick={() => setAttempt((count) => count + 1)}>Retry</Button>
    </> : <p role="status">Loading...</p>;
  }

  const choose = async () => {
    if (!choice || busy) return;
    setBusy('connecting');
    let connected: State | undefined;
    let refused = false;
    try {
      const { siteUrl } = await api.selectJiraSite(companyId, choice, pending.round);
      // The server connects only a workspace whose plan has Jira, read as it
      // connected, so the plan is known too (TOADO-713, Codex review round 3).
      connected = { status: 'connected', siteUrl, planHasJira: true };
      if (mounted.current) latest.current.onConnected(siteUrl);
    } catch (failure) {
      // Recorded for this workspace even when the picker is gone, after a
      // switch to another (Codex review round 5): `finally` writes it.
      refused = needsTeam(failure);
      if (!mounted.current) return;
      if (isNotFound(failure)) latest.current.onClose({ kind: 'error', message: GONE });
      // The plan dropped below Team meanwhile: the server has let the sign-in
      // go, so the picker closes rather than offering a retry (TOADO-713).
      else if (refused) latest.current.onClose({ kind: 'error', message: CALLBACK_ERRORS.jira_requires_team! });
      // Past its time the picker closes as expired, which says what to do.
      else if (Date.now() < deadline) failed('Could not connect that site');
    } finally {
      // Whatever the answer, or none, the workspace's status is read again: a
      // 404 can mean another tab disconnected meanwhile, and a lost answer
      // leaves the outcome unknown. A success still on screen shows at once,
      // and a refusal for the plan is the plan's answer.
      void (refused ? refusedForPlan(queryClient, companyId) : reconcile(queryClient, companyId, mounted.current ? connected : undefined));
      if (mounted.current) setBusy(null);
    }
  };
  // The picker closes once the server has let the sign-in go, not before: a
  // Cancel that failed leaves a grant that could still connect.
  const cancel = async () => {
    if (busy) return;
    setBusy('cancelling');
    try {
      await api.cancelJiraPending(companyId, pending.round);
      if (mounted.current) latest.current.onClose(null);
    } catch (failure) {
      if (!mounted.current) return;
      // The server answers a sign-in already gone with success; 404 is for
      // someone no longer an owner here, who has nothing left to cancel, and
      // a retry could never succeed (z.ai review round 3).
      if (isNotFound(failure)) latest.current.onClose(null);
      else failed('Could not cancel the sign-in');
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const chosen = pending.sites.find((site) => site.cloudId === choice);
  const warning = chosen && chosen.remapCount > 0 && pending.current ? remapWarning(chosen.remapCount, pending.current.siteUrl) : '';
  const minutes = minutesLeft(deadline - now);
  return (
    <form onSubmit={(event) => { event.preventDefault(); void choose(); }}>
      <fieldset ref={sites} tabIndex={-1} className={picker.sites}>
        <legend className={picker.legend}>Choose the Jira site for this workspace</legend>
        <p className={picker.hint}>
          This Atlassian account can reach {pending.sites.length} Jira sites, and a workspace connects to one.
          Choose within {minutes} {minutes === 1 ? 'minute' : 'minutes'}.
        </p>
        {pending.sites.map((site) => (
          <label key={site.cloudId} className={picker.site}>
            <input type="radio" name="jira-site" value={site.cloudId} checked={choice === site.cloudId}
              onChange={() => setChoice(site.cloudId)} disabled={busy !== null} />
            <span className={picker.siteText}>
              <span className={picker.siteName}>{site.name}</span>
              <span className={picker.siteUrl}>{host(site.url)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {/* One live region whose words change, so a reader hears the cost of each choice. */}
      <p role="status" className={warning ? picker.warning : picker.quiet}>{warning}</p>
      <div className={styles.row}>
        <Button type="submit" variant="primary" disabled={!choice || busy !== null}>{busy === 'connecting' ? 'Connecting...' : 'Connect'}</Button>
        <Button type="button" variant="secondary" onClick={() => void cancel()} disabled={busy !== null}>Cancel</Button>
      </div>
    </form>
  );
}

/**
 * Whether Toado hears from Jira that a sent issue was deleted (TOADO-712).
 * Nothing while that is switched off, or before the first sync.
 */
function DeletedIssuesLine({ watch, onConnectAgain }: {
  watch: { status: string | null; error: string | null } | null | undefined;
  onConnectAgain: () => void;
}) {
  if (!watch?.status) return null;
  if (watch.status === 'watching') return <p>When an issue Toado sent to a mapped Jira project is deleted in Jira, Toado unlinks its ticket.</p>;
  if (watch.status === 'needs_reconnect') return <p>
    To unlink a ticket when its issue is deleted in Jira, Toado needs one more permission.{' '}
    <Button variant="secondary" size="sm" onClick={onConnectAgain}>Connect again</Button>
  </p>;
  if (watch.status === 'no_public_url') return <p>Jira cannot reach this server, so an issue deleted in Jira is noticed when its ticket is opened.</p>;
  return <p role="alert">
    Toado could not ask Jira to say when an issue is deleted{watch.error ? `: ${watch.error}` : ''}. An issue deleted in Jira is still
    noticed when its ticket is opened.
  </p>;
}
